import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormatters, useI18n } from '../i18n';
import { useAuth } from '../lib/auth';
import { useLimits } from '../lib/useLimits';
import { runJob, uploadExport } from '../lib/api';
import { rememberResult } from '../lib/resultCache';
import { formatBytes, parseInstant } from '../lib/format';
import type { AnalysisKind, JobRequest } from '../lib/types';
import { Callout } from '../components/Callout';
import { ErrorNotice } from '../components/ErrorNotice';
import { ProgressPanel } from '../components/ProgressPanel';
import { AnalysisPicker } from '../features/analysis/AnalysisPicker';
import { ParameterForm } from '../features/analysis/ParameterForm';
import {
  analysisMessageKey,
  buildParameters,
  defaultParameterState,
  retargetParameterState,
  type ParameterState,
} from '../features/analysis/parameters';
import {
  OpenAlexQueryBuilder,
  emptyQueryState,
  queryIsEmpty,
  toOpenAlexQuery,
  type QueryFormState,
} from '../features/source/OpenAlexQueryBuilder';
import { FileDropzone } from '../features/source/FileDropzone';

type SourceTab = 'openalex' | 'upload';

interface RunState {
  stage: string;
  message?: string;
  percent?: number;
  history: string[];
  startedAt: number;
}

/** Home: choose a source, choose an analysis, run it. */
export function HomeRoute() {
  const { t, tag } = useI18n();
  const fmt = useFormatters();
  const navigate = useNavigate();
  const limits = useLimits();
  const { status, quota, signIn, refreshQuota } = useAuth();

  const [tab, setTab] = useState<SourceTab>('openalex');
  const [query, setQuery] = useState<QueryFormState>(emptyQueryState);
  const [file, setFile] = useState<File | null>(null);
  const [analysis, setAnalysis] = useState<AnalysisKind>('keyword_co_occurrence');
  const [params, setParams] = useState<ParameterState>(() =>
    defaultParameterState('keyword_co_occurrence'),
  );

  const [run, setRun] = useState<RunState | null>(null);
  const [error, setError] = useState<unknown>(null);
  const abort = useRef<AbortController | null>(null);

  const chooseAnalysis = (next: AnalysisKind) => {
    setAnalysis(next);
    setParams((current) => retargetParameterState(current, next));
  };

  const sourceReady = tab === 'openalex' ? !queryIsEmpty(query) : file !== null;
  const signedIn = status === 'signed-in';
  // An unlimited account reports remaining as -1, which satisfies `<= 0`.
  const outOfQuota = quota !== null && !quota.unlimited && quota.remaining <= 0;
  const canRun = sourceReady && signedIn && !outOfQuota && run === null;

  // The quota message has to carry the real reset time, not just "used up".
  const quotaResetsAt = parseInstant(quota?.resets_at);
  const quotaResets = quotaResetsAt ? fmt.dateTime(quotaResetsAt) : null;

  const advance = (stage: string, message?: string, percent?: number) =>
    setRun((current) => {
      const history = current ? current.history : [];
      return {
        stage,
        ...(message === undefined ? {} : { message }),
        ...(percent === undefined ? {} : { percent }),
        history: history.includes(stage) ? history : [...history, stage],
        startedAt: current ? current.startedAt : Date.now(),
      };
    });

  const start = async () => {
    setError(null);
    const controller = new AbortController();
    abort.current = controller;
    setRun({ stage: tab === 'upload' ? 'uploading' : 'queued', history: [], startedAt: Date.now() });

    try {
      let request: JobRequest;

      if (tab === 'upload') {
        if (!file) return;
        const receipt = await uploadExport(file, {
          signal: controller.signal,
          onProgress: (fraction) => advance('uploading', undefined, fraction),
        });
        request = {
          analysis,
          parameters: buildParameters(analysis, params),
          source: { kind: 'upload', upload_id: receipt.upload_id, filename: receipt.filename },
          name: defaultName(file.name, t(analysisMessageKey(analysis))),
        };
      } else {
        request = {
          analysis,
          parameters: buildParameters(analysis, params),
          source: { kind: 'openalex', query: toOpenAlexQuery(query, limits.max_documents) },
          name: defaultName(query.search.trim() || 'OpenAlex', t(analysisMessageKey(analysis))),
        };
      }

      advance('queued');

      const result = await runJob(request, {
        signal: controller.signal,
        onEvent: (event) => {
          if (event.type === 'progress') {
            advance(event.stage, event.message, event.percent);
          }
        },
      });

      rememberResult(result);
      refreshQuota();
      navigate(`/maps/${encodeURIComponent(result.map_id)}`);
    } catch (cause) {
      setError(cause);
      refreshQuota();
    } finally {
      abort.current = null;
      setRun(null);
    }
  };

  return (
    <div className="page">
      <div className="page__header">
        <h1 className="page__title">{t('home.title')}</h1>
        <p className="page__lead">{t('home.lead')}</p>
      </div>

      <section className="section" aria-labelledby="step-source">
        <h2 className="section__title" id="step-source">
          {t('home.step1')}
        </h2>

        <div className="panel">
          <div className="tabs" role="tablist">
            <button
              type="button"
              role="tab"
              className="tab"
              aria-selected={tab === 'openalex'}
              onClick={() => setTab('openalex')}
            >
              {t('source.tab.openalex')}
            </button>
            <button
              type="button"
              role="tab"
              className="tab"
              aria-selected={tab === 'upload'}
              onClick={() => setTab('upload')}
            >
              {t('source.tab.upload')}
            </button>
          </div>

          <div className="panel__body stack">
            {tab === 'openalex' ? (
              <>
                <p className="field__hint">{t('source.openalex.hint')}</p>
                <OpenAlexQueryBuilder
                  state={query}
                  onChange={setQuery}
                  maxDocuments={limits.max_documents}
                />
              </>
            ) : (
              <>
                <p className="field__hint">{t('source.upload.hint')}</p>
                <FileDropzone
                  file={file}
                  onFile={setFile}
                  maxBytes={limits.max_upload_bytes}
                  disabled={run !== null}
                />
              </>
            )}
          </div>
        </div>
      </section>

      <section className="section" aria-labelledby="step-analysis">
        <h2 className="section__title" id="step-analysis">
          {t('home.step2')}
        </h2>
        <div className="panel">
          <div className="panel__body stack">
            <AnalysisPicker value={analysis} onChange={chooseAnalysis} />
            <hr className="panel__divider" />
            <ParameterForm
              analysis={analysis}
              state={params}
              onChange={setParams}
              maxItemsCap={limits.max_items}
            />
          </div>
        </div>
      </section>

      <section className="section" aria-labelledby="step-run">
        <h2 className="section__title" id="step-run">
          {t('home.step3')}
        </h2>

        <div className="stack">
          <Callout tone="info" title={t('home.capTitle')}>
            <p>
              {t('home.capBody', {
                docs: fmt.int(limits.max_documents),
                items: fmt.int(limits.max_items),
              })}
            </p>
            <p className="muted">{t('home.capWhy')}</p>
            <p className="faint">
              {t('home.capUpload', { size: formatBytes(limits.max_upload_bytes, tag) })}
            </p>
          </Callout>

          {error ? <ErrorNotice error={error} onRetry={() => setError(null)} /> : null}

          {run ? (
            <ProgressPanel
              stage={run.stage}
              {...(run.message === undefined ? {} : { message: run.message })}
              {...(run.percent === undefined ? {} : { percent: run.percent })}
              history={run.history}
              startedAt={run.startedAt}
              onCancel={() => abort.current?.abort()}
            />
          ) : (
            <div className="stack stack--tight">
              <div className="button-row">
                <button
                  type="button"
                  className="button button--primary button--large"
                  disabled={!canRun}
                  onClick={() => void start()}
                >
                  {t('home.run')}
                </button>
                {!signedIn ? (
                  <button type="button" className="button" onClick={() => void signIn()}>
                    {t('header.signIn')}
                  </button>
                ) : null}
              </div>

              {!sourceReady ? <p className="field__hint">{t('home.runNoSource')}</p> : null}
              {!signedIn ? <p className="field__hint">{t('home.runNoAuth')}</p> : null}
              {signedIn && outOfQuota && quota ? (
                <p className="field__error">
                  {t('home.runNoQuota', {
                    limit: quota.limit,
                    resets: quotaResets ?? t('common.dash'),
                  })}
                </p>
              ) : null}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

/** A name the user will recognise in the saved list without having to open it. */
function defaultName(source: string, analysisLabel: string): string {
  const stem = source.length > 48 ? `${source.slice(0, 48)}…` : source;
  return `${stem} · ${analysisLabel}`;
}
